import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MonacoEditorConstructionOptions } from '@materia-ui/ngx-monaco-editor';
import { catchError, concatMap, debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { EMPTY, interval, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationResult, BrowserCacheLocation, PublicClientApplication } from '@azure/msal-browser';
import * as signalR from '@microsoft/signalr';

import { Icons } from '@vex/icons/icons';
import { DeviceService } from '@vex/http-service/device.service';
import { GlobalConsoleService } from '@vex/http-service/global-console.service';
import { AuthService } from '@vex/http-service/auth.service';
import { BlueprintService } from '@vex/http-service/blueprint.service';
import { DynamicTemplateService } from '@vex/http-service/dynamic-template.service';
import { InventoryService } from '@vex/http-service/inventory.service';
import { Style, StyleService } from '@vex/services/style.service';
import {
  GlobalConsoleExecution,
  GlobalConsoleLiveEvent,
  GlobalConsoleAccessChallenge,
  GlobalConsoleFile,
  GlobalConsoleMode,
  GlobalConsoleSession,
  GlobalConsoleSnapshot,
  GlobalConsoleStatus,
  GlobalConsoleWorkspace
} from '@vex/model/global-console';
import { IdentitySource } from '@vex/model/user';
import { buildEntraStepUpRequest } from './entra-step-up-request';

@Component({
  selector: 'vex-global-console',
  templateUrl: './global-console.component.html',
  styleUrls: ['./global-console.component.scss']
})
/**
 * Full-screen Global Console workspace coordinator.
 *
 * Durable state comes from the V2 API snapshot; raw output arrives on the private SignalR audience
 * and is held only in terminalBySession. Device selection reconciles one tab per selected device,
 * while broadcast submission creates independent executions so one target failure cannot overwrite
 * another target's state.
 */
export class GlobalConsoleComponent extends Icons implements OnInit, OnDestroy {
  @ViewChild('terminalOutput') private terminalOutput: ElementRef<HTMLElement>;

  readonly modes = [
    { value: GlobalConsoleMode.Device, label: 'Device' },
    { value: GlobalConsoleMode.Ansible, label: 'Ansible' },
    { value: GlobalConsoleMode.Terraform, label: 'Terraform' }
  ];
  readonly statusLabels = GlobalConsoleStatus;
  readonly GlobalConsoleMode = GlobalConsoleMode;
  readonly GlobalConsoleStatus = GlobalConsoleStatus;

  mode = GlobalConsoleMode.Device;
  workspace: GlobalConsoleWorkspace;
  workspaces: GlobalConsoleWorkspace[] = [];
  sessions: GlobalConsoleSession[] = [];
  executions: GlobalConsoleExecution[] = [];
  files: GlobalConsoleFile[] = [];
  activeSessionId: string;
  activeFileId: string;
  selectedDeviceIds: string[] = [];
  devices: any[] = [];
  /** Server-side device search shared by name and IP address through Device/GetForSelectPaginated. */
  deviceSearchCtrl = new FormControl('');
  freeSsh = false;
  broadcast = true;
  command = '';
  loading = false;
  accessVerified = false;
  liveConnected = false;
  accessLoading = false;
  riskAccepted = false;
  localPassword = '';
  emailCode = '';
  smsCode = '';
  accessChallenge: GlobalConsoleAccessChallenge;
  accessError = '';
  targetSyncing = false;
  devicesLoading = false;
  historyOpen = false;
  historyLoading = false;
  historyPageIndex = 0;
  historyPageSize = 10;
  historyTotalCount = 0;
  /** Live-only terminal buffers keyed by session ID; never persist these strings client-side. */
  terminalBySession: Record<string, string> = {};
  activeInteractiveExecution: Record<string, string> = {};
  prefillType = 'Template';
  prefillId: string;
  prefillOptions: any[] = [];
  private destroy$ = new Subject<void>();
  private targetChanges$ = new Subject<void>();
  private pendingSessionEvents: Record<string, GlobalConsoleLiveEvent> = {};
  private interactivePollRequestedAt = 0;
  private closingSessionIds = new Set<string>();
  private pendingCommandSessionIds = new Set<string>();
  private workspaceInitialized = false;
  private consoleHub: signalR.HubConnection;

  editorOptions: MonacoEditorConstructionOptions = {
    theme: localStorage.getItem('styleMode') === 'light' ? 'vs' : 'vs-dark',
    language: 'yaml',
    automaticLayout: true,
    minimap: { enabled: false },
    wordWrap: 'on'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { DeviceId?: string },
    public dialogRef: MatDialogRef<GlobalConsoleComponent>,
    private consoleService: GlobalConsoleService,
    private authService: AuthService,
    private deviceService: DeviceService,
    private blueprintService: BlueprintService,
    private dynamicTemplateService: DynamicTemplateService,
    private inventoryService: InventoryService,
    private styleService: StyleService,
    private toastr: ToastrService
  ) { super(); }

  ngOnInit(): void {
    this.consoleService.beginPopup();
    this.styleService.style$.pipe(takeUntil(this.destroy$)).subscribe(style => {
      this.editorOptions = { ...this.editorOptions, theme: style === Style.dark ? 'vs-dark' : 'vs' };
    });
  }

  get isLocalUser(): boolean {
    return this.authService.getIdentitySource() !== IdentitySource.ActiveDirectory;
  }

  get canBypassMfa(): boolean {
    return this.authService.isNotRequired2FA();
  }

  beginAccessVerification(): void {
    if (!this.riskAccepted || this.accessLoading || (this.isLocalUser && !this.canBypassMfa && !this.localPassword)) return;
    this.accessLoading = true;
    this.accessError = '';
    this.consoleService.startAccessChallenge(this.localPassword, this.riskAccepted)
      .pipe(takeUntil(this.destroy$), finalize(() => this.accessLoading = false))
      .subscribe({
        next: challenge => {
          this.localPassword = '';
          this.accessChallenge = challenge;
          if (challenge.Grant) {
            this.unlockConsole();
          } else if (challenge.Provider === 'MicrosoftEntra') {
            this.accessLoading = false;
            this.verifyWithEntra(challenge);
          }
        },
        error: error => this.accessError = this.accessErrorMessage(error, 'Access verification could not be started.')
      });
  }

  verifyLocalAccess(): void {
    if (!this.accessChallenge || !/^\d{6}$/.test(this.emailCode) || !/^\d{6}$/.test(this.smsCode) || this.accessLoading) return;
    this.accessLoading = true;
    this.accessError = '';
    this.consoleService.verifyLocal(this.accessChallenge.Id, this.emailCode, this.smsCode)
      .pipe(takeUntil(this.destroy$), finalize(() => this.accessLoading = false))
      .subscribe({
        next: () => this.unlockConsole(),
        error: error => this.accessError = this.accessErrorMessage(error, 'The verification code was not accepted.')
      });
  }

  async verifyWithEntra(challenge: GlobalConsoleAccessChallenge = this.accessChallenge): Promise<void> {
    if (!challenge?.ClientId || !challenge?.TenantId || this.accessLoading) return;
    this.accessLoading = true;
    this.accessError = '';
    try {
      const authority = `https://login.microsoftonline.com/${challenge.TenantId}`;
      const pca = new PublicClientApplication({
        auth: { clientId: challenge.ClientId, authority, redirectUri: window.location.origin + '/login/' },
        cache: { cacheLocation: BrowserCacheLocation.SessionStorage, storeAuthStateInCookie: false }
      });
      const request = buildEntraStepUpRequest({
        authority,
        loginHint: challenge.LoginHint,
        authenticationContext: challenge.AuthenticationContext
      });
      const response: AuthenticationResult = await pca.loginPopup(request);
      this.consoleService.verifyEntra(challenge.Id, response.accessToken, response.idToken)
        .pipe(takeUntil(this.destroy$), finalize(() => this.accessLoading = false))
        .subscribe({
          next: () => this.unlockConsole(),
          error: error => this.accessError = this.accessErrorMessage(error, 'Microsoft Entra MFA was not accepted.')
        });
    } catch (error) {
      this.accessLoading = false;
      const code = (error as any)?.errorCode;
      if (code !== 'user_cancelled') this.accessError = 'Microsoft Entra MFA was not completed.';
    }
  }

  private unlockConsole(): void {
    this.accessVerified = true;
    this.emailCode = '';
    this.smsCode = '';
    this.initializeWorkspace();
  }

  private initializeWorkspace(): void {
    if (this.workspaceInitialized) return;
    this.workspaceInitialized = true;
    this.subscribeToOutput();
    this.subscribeToTargetChanges();
    this.deviceSearchCtrl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(search => this.loadDevices(String(search || '').trim()));
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => this.pollInteractiveOutput());
    this.loadHistory();
    this.loadDevices();
  }

  ngOnDestroy(): void {
    if (this.consoleHub) this.consoleHub.stop().catch(() => undefined);
    this.consoleService.clearAccess();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.accessVerified = false;
    if (this.accessVerified) {
      this.endVisibleSessions();
      this.consoleService.revokeAccess().subscribe({ error: () => {} });
    }
    this.consoleService.clearAccess();
    this.dialogRef.close();
  }

  private accessErrorMessage(error: any, fallback: string): string {
    const body = error?.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (typeof body?.text === 'string' && body.text.trim()) return body.text;
    if (body?.Message || body?.message || body?.ErrorMessage || body?.errorMessage) {
      return body.Message || body.message || body.ErrorMessage || body.errorMessage;
    }
    if (error?.status === 0) return 'The API could not be reached. Confirm the local server is running and try again.';
    if (error?.status === 401) return 'Your application session is no longer authorized. Sign in again and retry.';
    return fallback;
  }

  selectMode(mode: GlobalConsoleMode): void {
    if (this.mode === mode && this.workspace) return;
    if (this.sessions.some(session => this.isLive(session.Status)) &&
      !window.confirm('Changing mode keeps history but ends the current visual context. Continue?')) return;
    this.endVisibleSessions();
    this.mode = mode;
    this.freeSsh = false;
    this.selectedDeviceIds = [];
    this.sessions = [];
    this.executions = [];
    this.files = [];
    this.workspace = null;
    this.activeSessionId = null;
    this.activeFileId = null;
    this.editorOptions = { ...this.editorOptions, language: mode === GlobalConsoleMode.Ansible ? 'yaml' : mode === GlobalConsoleMode.Terraform ? 'hcl' : 'text' };
    if (mode !== GlobalConsoleMode.Device) {
      this.createWorkspace(mode);
      this.loadPrefillOptions();
    }
  }

  createWorkspace(mode: GlobalConsoleMode): void {
    if (this.loading || this.workspace) return;
    this.loading = true;
    this.consoleService.createWorkspace(mode)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({
        next: workspace => {
          this.workspace = workspace;
          if (mode !== GlobalConsoleMode.Device) this.createAutomationSession();
          else if (this.freeSsh || this.selectedDeviceIds.length) {
            this.targetSyncing = true;
            this.targetChanges$.next();
          }
          this.loadHistory(0);
        },
        error: () => this.toastr.error('Unable to create the console workspace.')
      });
  }

  openWorkspace(item: GlobalConsoleWorkspace): void {
    this.loading = true;
    this.consoleService.getWorkspace(item.Id)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({ next: snapshot => this.applySnapshot(snapshot), error: () => this.toastr.error('Unable to restore this workspace.') });
  }

  loadHistory(pageIndex = this.historyPageIndex): void {
    this.historyLoading = true;
    this.consoleService.listWorkspaces(pageIndex, this.historyPageSize)
      .pipe(takeUntil(this.destroy$), finalize(() => this.historyLoading = false))
      .subscribe({
        next: page => {
          this.workspaces = page?.items || [];
          this.historyPageIndex = pageIndex;
          this.historyTotalCount = page?.totalCount || 0;
        },
        error: () => this.toastr.error('Console history could not be loaded.')
      });
  }

  onHistoryPage(event: PageEvent): void {
    this.historyPageSize = event.pageSize;
    this.loadHistory(event.pageIndex);
  }

  loadDevices(search = ''): void {
    this.devicesLoading = true;
    const selectedItemIds = Array.from(new Set([
      ...this.selectedDeviceIds,
      ...(this.data?.DeviceId ? [this.data.DeviceId] : [])
    ]));
    this.deviceService.getForSelectPaginated({
      Filter: search, PageNumber: 0, PageSize: 100, DeviceConnectivityStatus: 1,
      SelectedItemIds: selectedItemIds
    }).pipe(takeUntil(this.destroy$), finalize(() => this.devicesLoading = false)).subscribe({
      next: (response: any) => {
        const available = [...(response?.selectedItems || []), ...(response?.items || [])];
        this.devices = available.filter((device, index, items) =>
          items.findIndex(item => (item.Value || item.Id) === (device.Value || device.Id)) === index);
        if (this.data?.DeviceId && !this.selectedDeviceIds.length) {
          if (this.devices.some(device => (device.Value || device.Id) === this.data.DeviceId)) {
            this.selectedDeviceIds = [this.data.DeviceId];
            if (!this.workspace && this.mode === GlobalConsoleMode.Device) {
              this.createWorkspace(GlobalConsoleMode.Device);
            } else if (this.workspace && this.mode === GlobalConsoleMode.Device) {
              this.targetSyncing = true;
              this.targetChanges$.next();
            }
          } else {
            this.toastr.warning('The requested device is not currently connected.');
          }
        }
      },
      error: () => {
        this.devices = [];
        this.toastr.error('Connected devices could not be loaded.');
      }
    });
  }

  onDeviceSelectionChange(deviceIds: string[]): void {
    this.selectedDeviceIds = deviceIds || [];
    if (this.selectedDeviceIds.length) this.freeSsh = false;
    if (!this.workspace && this.selectedDeviceIds.length) {
      this.createWorkspace(GlobalConsoleMode.Device);
      return;
    }
    if (this.workspace) this.targetSyncing = true;
    this.targetChanges$.next();
  }

  onFreeSshChange(enabled: boolean): void {
    this.freeSsh = enabled;
    if (enabled) this.selectedDeviceIds = [];
    if (!this.workspace && enabled) {
      this.createWorkspace(GlobalConsoleMode.Device);
      return;
    }
    if (this.workspace) this.targetSyncing = true;
    this.targetChanges$.next();
  }

  createAutomationSession(): void {
    const target = this.mode === GlobalConsoleMode.Ansible
      ? GlobalConsoleMode.Ansible : GlobalConsoleMode.Terraform;
    this.consoleService.createSessions(this.workspace.Id, target)
      .pipe(takeUntil(this.destroy$)).subscribe(response => {
        if (response?.Workspace) this.workspace = response.Workspace;
        this.sessions = response.Sessions || [];
        this.activeSessionId = this.sessions[0]?.Id;
        this.loadHistory(0);
      });
  }

  run(): void {
    const session = this.activeSession;
    const text = this.command;
    if (!this.liveConnected || !session || !text.trim() || this.loading) return;

    if (session.Mode === GlobalConsoleMode.FreeSsh && this.activeInteractiveExecution[session.Id]) {
      this.consoleService.sendInput(session.Id, this.activeInteractiveExecution[session.Id], text + '\n')
        .pipe(takeUntil(this.destroy$)).subscribe({ error: () => this.toastr.error('Unable to send terminal input.') });
      this.command = '';
      return;
    }

    if (this.mode === GlobalConsoleMode.Device && this.broadcast && this.sessions.length > 1 &&
      !window.confirm(`Run this command on all ${this.sessions.length} selected devices?`)) return;
    const isApply = this.mode === GlobalConsoleMode.Terraform && /^\s*(terraform|tofu)\s+apply\b/i.test(text);
    if (isApply && !window.confirm('Terraform apply can change infrastructure. Run apply with the current workspace files?')) return;
    const targetSessions = this.mode === GlobalConsoleMode.Device && this.broadcast && this.sessions.length > 1
      ? [...this.sessions] : [session];
    this.beginOptimisticCommand(targetSessions, text);
    this.command = '';
    this.loading = true;
    const request = this.mode === GlobalConsoleMode.Device && this.broadcast && this.sessions.length > 1
      ? this.consoleService.broadcast(session.SessionGroupId, text)
      : this.consoleService.runCommand(session.Id, text, this.operationFor(text), this.mode === GlobalConsoleMode.Device ? 180 : 3600, isApply);
    request.pipe(takeUntil(this.destroy$), finalize(() => this.loading = false)).subscribe({
      next: (response: any) => {
        targetSessions.forEach(target => this.pendingCommandSessionIds.delete(target.Id));
        if (response?.Id) this.trackExecution(response, false);
        (response?.Targets || []).forEach(target => target?.Id && this.trackExecution(target, false));
        if (session.Mode === GlobalConsoleMode.FreeSsh && response?.Id) this.activeInteractiveExecution[session.Id] = response.Id;
      },
      error: error => {
        targetSessions.forEach(target => {
          this.pendingCommandSessionIds.delete(target.Id);
          target.Status = GlobalConsoleStatus.Ready;
        });
        this.command = text;
        this.toastr.error(error?.error || 'Command could not be queued.');
      }
    });
  }

  /** Sends on Enter while allowing Shift+Enter to add another command line. */
  onCommandKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    this.run();
  }

  addFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    Array.from(input.files || []).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => this.saveFile({ Name: file.name, Content: String(reader.result || ''), Sensitive: file.name.endsWith('.tfstate') });
      reader.readAsText(file);
    });
    input.value = '';
  }

  loadPrefillOptions(): void {
    this.prefillId = null;
    const filter = { Filter: '', PageNumber: 0, PageSize: 100 };
    const request = this.prefillType === 'Template' ? this.dynamicTemplateService.getForSelectPaginated(filter)
      : this.prefillType === 'Blueprint' ? this.blueprintService.getForSelectPaginated(filter)
      : this.inventoryService.getForSelectPaginated(filter);
    request.pipe(takeUntil(this.destroy$)).subscribe((response: any) => this.prefillOptions = response?.items || []);
  }

  applyPrefill(): void {
    if (!this.prefillId || !this.workspace) return;
    this.consoleService.prefill(this.workspace.Id, this.prefillType, this.prefillId)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: files => {
          (files || []).forEach(file => {
            const index = this.files.findIndex(item => item.Id === file.Id);
            if (index >= 0) this.files[index] = file; else this.files.push(file);
            this.activeFileId = file.Id;
          });
        },
        error: error => this.toastr.error(error?.error || 'The selected artifact could not be prefilled.')
      });
  }

  newFile(): void {
    const name = window.prompt('File name', this.mode === GlobalConsoleMode.Ansible ? 'playbook.yaml' : 'main.tf');
    if (name) this.saveFile({ Name: name, Content: '', Sensitive: name.endsWith('.tfstate') });
  }

  saveActiveFile(): void {
    if (this.activeFile) this.saveFile(this.activeFile);
  }

  deleteFile(file: GlobalConsoleFile, event: Event): void {
    event.stopPropagation();
    if (!window.confirm(`Remove ${file.Name} from this workspace? History and audit evidence remain.`)) return;
    this.consoleService.deleteFile(this.workspace.Id, file.Id).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.files = this.files.filter(item => item.Id !== file.Id);
      if (this.activeFileId === file.Id) this.activeFileId = this.files[0]?.Id;
    });
  }

  selectFile(file: GlobalConsoleFile): void {
    this.activeFileId = file.Id;
    this.editorOptions = { ...this.editorOptions, language: file.Name.endsWith('.yaml') || file.Name.endsWith('.yml') ? 'yaml' : file.Name.endsWith('.json') ? 'json' : file.Name.endsWith('.tf') ? 'hcl' : 'text' };
  }

  endSession(session: GlobalConsoleSession, event?: Event): void {
    event?.stopPropagation();
    if (this.closingSessionIds.has(session.Id)) return;
    this.closingSessionIds.add(session.Id);
    if (session.Mode === GlobalConsoleMode.Device) {
      this.selectedDeviceIds = this.selectedDeviceIds.filter(id => id !== session.SelectedDeviceId);
      this.targetSyncing = true;
      this.targetChanges$.next();
      return;
    }
    if (session.Mode === GlobalConsoleMode.FreeSsh) {
      this.freeSsh = false;
      this.targetSyncing = true;
      this.targetChanges$.next();
      return;
    }
    this.consoleService.endSession(session.Id).pipe(takeUntil(this.destroy$), finalize(() => this.closingSessionIds.delete(session.Id)))
      .subscribe(() => session.Status = GlobalConsoleStatus.Ended);
  }

  clearView(): void {
    if (this.activeSessionId) this.terminalBySession[this.activeSessionId] = '';
  }

  copyOutput(): void {
    navigator.clipboard?.writeText(this.activeOutput || '');
  }

  downloadOutput(): void {
    const blob = new Blob([this.activeOutput || ''], { type: 'text/plain;charset=utf-8' });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${this.activeSession?.Name || 'console'}-transcript.txt`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  deviceLabel(device: any): string {
    const name = device?.Label || device?.Name;
    const ipAddress = device?.IpAddress;
    return name && ipAddress ? `${name} (${ipAddress})` : name || ipAddress || device?.Value || device?.Id;
  }

  statusLabel(status: GlobalConsoleStatus): string { return GlobalConsoleStatus[status] || 'Ready'; }
  isSessionClosing(sessionId: string): boolean { return this.closingSessionIds.has(sessionId); }
  isSessionProcessing(session: GlobalConsoleSession): boolean {
    return !!session && (this.pendingCommandSessionIds.has(session.Id) || this.isLive(session.Status));
  }
  processingPhase(session: GlobalConsoleSession): string {
    if (!session) return '';
    if (this.pendingCommandSessionIds.has(session.Id)) return 'dispatching';
    if (session.ProtectedInputActive) return 'protected-input';
    if (session.Mode === GlobalConsoleMode.FreeSsh && this.activeInteractiveExecution[session.Id]) return 'interactive';
    switch (session.Status) {
      case GlobalConsoleStatus.Queued: return 'queued';
      case GlobalConsoleStatus.Connecting: return 'connecting';
      case GlobalConsoleStatus.Running: return 'running';
      default: return '';
    }
  }
  isCommandUnavailable(session: GlobalConsoleSession): boolean {
    if (!session || this.isSessionClosing(session.Id)) return true;
    if (session.Mode === GlobalConsoleMode.FreeSsh && this.activeInteractiveExecution[session.Id]) return false;
    return [GlobalConsoleStatus.Queued, GlobalConsoleStatus.Connecting, GlobalConsoleStatus.Running].includes(session.Status);
  }
  trackById(_: number, item: any): string { return item.Id; }

  get activeSession(): GlobalConsoleSession { return this.sessions.find(item => item.Id === this.activeSessionId); }
  get activeFile(): GlobalConsoleFile { return this.files.find(item => item.Id === this.activeFileId); }
  get activeOutput(): string { return this.terminalBySession[this.activeSessionId] || ''; }

  private saveFile(file: Partial<GlobalConsoleFile>): void {
    this.consoleService.saveFile(this.workspace.Id, file).pipe(takeUntil(this.destroy$)).subscribe({
      next: saved => {
        const index = this.files.findIndex(item => item.Id === saved.Id);
        if (index >= 0) this.files[index] = saved; else this.files.push(saved);
        this.activeFileId = saved.Id;
      },
      error: error => this.toastr.error(error?.error || 'The workspace file could not be saved.')
    });
  }

  private subscribeToOutput(): void {
    this.consoleService.negotiateLiveConnection().pipe(takeUntil(this.destroy$)).subscribe({
      next: connectionInfo => {
        const url = connectionInfo?.Url || connectionInfo?.url;
        const accessToken = connectionInfo?.AccessToken || connectionInfo?.accessToken;
        if (!url || !accessToken) {
          this.toastr.error('Secure live console connection could not be negotiated.');
          return;
        }
        this.consoleHub = new signalR.HubConnectionBuilder().withUrl(url, {
          accessTokenFactory: () => accessToken,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets
        }).build();
        this.consoleHub.serverTimeoutInMilliseconds = 120000;
        this.consoleHub.keepAliveIntervalInMilliseconds = 30000;
        this.consoleHub.on('GlobalConsoleV2Event', (event: any) => this.handleOutputEvent(event));
        this.consoleHub.onclose(() => {
          this.liveConnected = false;
          if (this.accessVerified) this.subscribeToOutput();
        });
        this.consoleHub.start().then(() => this.liveConnected = true)
          .catch(() => this.toastr.error('Secure live console connection could not be started.'));
      },
      error: () => this.toastr.error('Secure live console connection could not be negotiated.')
    });
  }

  private handleOutputEvent(event: GlobalConsoleLiveEvent): void {
      if (!event || event.WorkspaceId !== this.workspace?.Id) return;
      const session = this.sessions.find(item => item.Id === event.SessionId);
      const sequence = Number(event.Sequence);
      if (session && Number.isFinite(sequence) && sequence > 0 && sequence <= (session.LastSequence || 0)) return;
      if (session) {
        if (Number.isFinite(sequence) && sequence > 0) session.LastSequence = sequence;
        session.Status = event.SessionStatus !== null && event.SessionStatus !== undefined ? event.SessionStatus : event.Status;
        if (event.InputProtected !== null && event.InputProtected !== undefined) session.ProtectedInputActive = event.InputProtected;
      } else if (event.SessionId) {
        const pending = this.pendingSessionEvents[event.SessionId];
        if (!pending || !Number.isFinite(sequence) || sequence >= Number(pending.Sequence || 0)) {
          this.pendingSessionEvents[event.SessionId] = event;
        }
      }
      if (event.Text) {
        this.terminalBySession[event.SessionId] = (this.terminalBySession[event.SessionId] || '') + event.Text;
        this.scrollTerminalToEnd();
      }
      if (event.ExecutionId && event.ExecutionId === this.activeInteractiveExecution[event.SessionId]) this.interactivePollRequestedAt = 0;
      if (this.isTerminal(event.Status)) {
        delete this.activeInteractiveExecution[event.SessionId];
        if (session) session.ProtectedInputActive = false;
      }
      const execution = this.executions.find(item => item.Id === event.ExecutionId);
      if (execution) execution.Status = event.Status;
  }

  private subscribeToTargetChanges(): void {
    this.targetChanges$.pipe(
      debounceTime(150),
      concatMap(() => {
        if (!this.workspace || this.mode !== GlobalConsoleMode.Device) return EMPTY;
        this.targetSyncing = true;
        const target = this.freeSsh ? GlobalConsoleMode.FreeSsh : GlobalConsoleMode.Device;
        const ids = this.freeSsh ? [] : [...this.selectedDeviceIds];
        return this.consoleService.createSessions(this.workspace.Id, target, ids).pipe(
          catchError(error => {
            this.closingSessionIds.clear();
            this.freeSsh = this.sessions.some(session => session.Mode === GlobalConsoleMode.FreeSsh);
            this.selectedDeviceIds = this.sessions
              .filter(session => session.Mode === GlobalConsoleMode.Device)
              .map(session => session.SelectedDeviceId);
            this.toastr.error(error?.error || 'Unable to update terminal targets.');
            return EMPTY;
          }),
          finalize(() => this.targetSyncing = false)
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      const priorActive = this.activeSessionId;
      if (response?.Workspace) this.workspace = response.Workspace;
      this.sessions = response?.Sessions || [];
      this.closingSessionIds.clear();
      this.sessions.forEach(session => {
        const event = this.pendingSessionEvents[session.Id];
        if (!event) return;
        session.Status = event.SessionStatus !== null && event.SessionStatus !== undefined ? event.SessionStatus : event.Status;
        if (Number.isFinite(Number(event.Sequence)) && Number(event.Sequence) > 0) session.LastSequence = Number(event.Sequence);
        if (event.InputProtected !== null && event.InputProtected !== undefined) session.ProtectedInputActive = event.InputProtected;
        delete this.pendingSessionEvents[session.Id];
      });
      this.activeSessionId = this.sessions.some(session => session.Id === priorActive) ? priorActive : this.sessions[0]?.Id;
      this.sessions.forEach(session => this.terminalBySession[session.Id] = this.terminalBySession[session.Id] || '');
      this.loadHistory(0);
    });
  }

  private pollInteractiveOutput(): void {
    const session = this.activeSession;
    const executionId = session && this.activeInteractiveExecution[session.Id];
    if (!session || session.Mode !== GlobalConsoleMode.FreeSsh || !executionId ||
      (this.interactivePollRequestedAt && Date.now() - this.interactivePollRequestedAt < 15000)) return;
    this.interactivePollRequestedAt = Date.now();
    this.consoleService.pollSession(session.Id, executionId).pipe(takeUntil(this.destroy$)).subscribe({
      error: () => this.interactivePollRequestedAt = 0
    });
  }

  private endVisibleSessions(): void {
    this.sessions.filter(session => session.Status !== GlobalConsoleStatus.Ended).forEach(session => {
      this.consoleService.endSession(session.Id).subscribe({ error: () => undefined });
    });
  }

  private applySnapshot(snapshot: GlobalConsoleSnapshot): void {
    this.workspace = snapshot.Workspace;
    this.mode = snapshot.Workspace.Mode;
    this.editorOptions = { ...this.editorOptions, language: this.mode === GlobalConsoleMode.Ansible ? 'yaml' : this.mode === GlobalConsoleMode.Terraform ? 'hcl' : 'text' };
    this.sessions = snapshot.Sessions || [];
    this.freeSsh = this.sessions.some(session => session.Mode === GlobalConsoleMode.FreeSsh && session.Status !== GlobalConsoleStatus.Ended);
    this.selectedDeviceIds = this.sessions
      .filter(session => session.Mode === GlobalConsoleMode.Device && session.Status !== GlobalConsoleStatus.Ended)
      .map(session => session.SelectedDeviceId);
    this.files = snapshot.Files || [];
    this.executions = snapshot.Executions || [];
    this.activeSessionId = this.sessions[0]?.Id;
    this.activeFileId = this.files[0]?.Id;
    this.terminalBySession = {};
    this.executions.forEach(execution => {
      this.terminalBySession[execution.SessionId] = (this.terminalBySession[execution.SessionId] || '') +
        `› ${execution.Command}\n` + (execution.Transcript || []).sort((a, b) => a.Sequence - b.Sequence).map(chunk => chunk.Text).join('');
    });
    this.sessions.filter(session => session.Mode === GlobalConsoleMode.FreeSsh).forEach(session => {
      const running = [...this.executions].reverse().find(execution => execution.SessionId === session.Id &&
        [GlobalConsoleStatus.Queued, GlobalConsoleStatus.Connecting, GlobalConsoleStatus.Running].includes(execution.Status));
      if (running) this.activeInteractiveExecution[session.Id] = running.Id;
    });
    this.historyOpen = false;
    if (this.mode === GlobalConsoleMode.Device) this.loadDevices();
  }

  private trackExecution(execution: GlobalConsoleExecution, echoCommand = true): void {
    this.executions.push(execution);
    const session = this.sessions.find(item => item.Id === execution.SessionId);
    if (session) session.Status = GlobalConsoleStatus.Queued;
    if (echoCommand) {
      this.terminalBySession[execution.SessionId] = (this.terminalBySession[execution.SessionId] || '') + `> ${execution.Command}\n`;
      this.scrollTerminalToEnd();
    }
  }

  private beginOptimisticCommand(sessions: GlobalConsoleSession[], command: string): void {
    sessions.forEach(session => {
      this.pendingCommandSessionIds.add(session.Id);
      session.Status = GlobalConsoleStatus.Queued;
      this.terminalBySession[session.Id] = (this.terminalBySession[session.Id] || '') + `> ${command}\n`;
    });
    this.scrollTerminalToEnd();
  }

  private scrollTerminalToEnd(): void {
    setTimeout(() => {
      const output = this.terminalOutput?.nativeElement;
      if (output) output.scrollTop = output.scrollHeight;
    });
  }

  private operationFor(command: string): string {
    const parts = command.trim().split(/\s+/);
    return this.mode === GlobalConsoleMode.Terraform && parts.length > 1 ? parts[1] : 'command';
  }

  private isLive(status: GlobalConsoleStatus): boolean {
    return [GlobalConsoleStatus.Queued, GlobalConsoleStatus.Connecting, GlobalConsoleStatus.Running].includes(status);
  }

  private isTerminal(status: GlobalConsoleStatus): boolean {
    return [GlobalConsoleStatus.Succeeded, GlobalConsoleStatus.Failed, GlobalConsoleStatus.TimedOut, GlobalConsoleStatus.Cancelled].includes(status);
  }
}
